// Cross-package contract types live in @rondel/shared so the relay/agent-
// manager and the frontend can't drift on them silently. Re-exported here
// for the existing `import { ... } from "./types"` callsites.
export type { AgentStatus, SessionInfo } from "@rondel/shared";
import type { AgentStatus, SessionInfo } from "@rondel/shared";

// Frontend-only — not part of the relay contract; the orchestrator's row
// shape is the source of truth and lives in `packages/orchestrator/src/db.ts`.
export interface DeskRow {
  id: string;
  user_id: string;
  name: string;
  sprite_name: string | null;
  status: string;
  relay_url: string | null;
  relay_auth_token: string | null;
  agent_type: string;
  model: string | null;
  system_prompt: string | null;
  created_at: string;
  last_opened_at: string | null;
  total_spend_usd: number;
}

export const AGENT_STATUS_META: Record<
  AgentStatus,
  { label: string; tone: "accent" | "running" | "ok" | "err" | "warn" }
> = {
  starting: { label: "Starting", tone: "accent" },
  running: { label: "Running", tone: "running" },
  completed: { label: "Done", tone: "ok" },
  failed: { label: "Failed", tone: "err" },
  interrupted: { label: "Stopped", tone: "warn" },
  stale: { label: "Stale", tone: "warn" },
};

export interface FileEntry {
  name: string;
  path: string;
  isDir: boolean;
  size: number;
  modTime: string | null;
}

export interface EventEntry {
  type: string;
  timestamp?: string;
  [key: string]: unknown;
}

export interface HealthResponse {
  status: string;
  uptimeMs?: number;
  sessions?: number;
  service?: string;
}

export interface SpendResponse {
  userId?: string;
  deskId?: string;
  totalSpendUsd: number;
  spendLimitUsd: number | null;
  deskCount?: number;
}

export interface ConnectionInfo {
  relayUrl: string | null;
  relayAuthToken: string | null;
  spriteName: string | null;
  status: string;
}

/**
 * User-facing desk state — derived from real signals, not just API status.
 * - active:  sprite running, relay healthy, agent ops available
 * - waking:  in transition (user or auto-triggered)
 * - warm:    sprite warm, resumes fast
 * - stored:  sprite cold, FS API works but first read slower
 * - booting: sprite being created, FS API not ready yet
 * - error:   sprite in terminal failure
 */
export type DeskState =
  | "active"
  | "waking"
  | "warm"
  | "stored"
  | "booting"
  | "error";

export function deriveDeskState(desk: DeskRow): DeskState {
  const s = desk.status;
  if (s === "creating") return "booting";
  if (s === "error") return "error";
  if (s === "ready") return "active";
  if (s === "waking") return "waking";
  if (s === "cold") return desk.sprite_name ? "stored" : "error";
  return "warm";
}

export const DESK_STATE_META: Record<
  DeskState,
  { label: string; desc: string; tone: "ok" | "warm" | "cold" | "err" | "accent" }
> = {
  active: { label: "Active", desc: "Ready for work", tone: "ok" },
  waking: { label: "Waking", desc: "Starting up…", tone: "warm" },
  warm: { label: "Warm", desc: "Quick to resume", tone: "warm" },
  stored: { label: "Stored", desc: "Data preserved", tone: "cold" },
  booting: { label: "Deploying", desc: "Setting up sprite", tone: "accent" },
  error: { label: "Error", desc: "Needs attention", tone: "err" },
};

const ACCENT_COLORS = [
  "var(--da-1)",
  "var(--da-2)",
  "var(--da-3)",
  "var(--da-4)",
  "var(--da-5)",
  "var(--da-6)",
  "var(--da-7)",
];

export function deskAccentColor(index: number): string {
  return ACCENT_COLORS[index % ACCENT_COLORS.length];
}

export function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "";
  const ts = new Date(dateStr + (dateStr.includes("Z") ? "" : "Z")).getTime();
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 5) return "now";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/**
 * Age in seconds of a timestamp, or null if no date.
 * Used to detect stale sessions (agent hung, no activity for N seconds).
 */
export function ageSeconds(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const ts = new Date(dateStr + (dateStr.includes("Z") ? "" : "Z")).getTime();
  return Math.floor((Date.now() - ts) / 1000);
}

/**
 * Effective session state — combines agentStatus with liveness signals to
 * distinguish a truly running agent from a hung/zombie one.
 *
 * - running:  agentStatus running + lastActivity recent (< 90s)
 * - thinking: agentStatus running but no events for a while (90s-5min)
 * - stalled:  agentStatus running but lastActivity very old (> 5min) OR manager died
 * - starting/completed/failed/interrupted/stale: as-is
 */
export type EffectiveSessionState =
  | "starting"
  | "running"
  | "thinking"
  | "stalled"
  | "completed"
  | "failed"
  | "interrupted";

export function deriveSessionState(s: SessionInfo): EffectiveSessionState {
  if (s.agentStatus === "completed") return "completed";
  if (s.agentStatus === "failed") return "failed";
  if (s.agentStatus === "interrupted") return "interrupted";
  if (s.agentStatus === "stale") return "stalled";

  const age = ageSeconds(s.lastActivityAt);

  if (s.agentStatus === "starting") {
    // A "starting" session that's been starting > 90s without progress is
    // likely stuck (agent-manager stdout-poll bug, network hiccup, etc.).
    // Fall through to stalled so the user knows to reset.
    if (!s.managerAlive) return "stalled";
    if (age != null && age > 180) return "stalled";
    return "starting";
  }

  // agentStatus === "running" → refine based on liveness
  if (!s.managerAlive) return "stalled";
  if (age === null) return "starting";
  if (age > 300) return "stalled"; // 5+ min no activity = likely hung
  if (age > 90) return "thinking"; // 90s-5min = probably thinking hard
  return "running";
}

/**
 * A session that was started on a sprite that's now cold, before the agent
 * ever emitted a session_id. There's no agentSessionId on metadata, so we
 * can't --resume it. The only sane path forward is to start a new session.
 */
export function isSessionUnresumable(s: SessionInfo): boolean {
  return (
    !s.managerAlive &&
    s.agentSessionId === null &&
    (s.agentStatus === "completed" ||
      s.agentStatus === "failed" ||
      s.agentStatus === "interrupted" ||
      s.agentStatus === "stale")
  );
}
